import {
  AmbientLight,
  DirectionalLight,
  MathUtils,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer,
} from 'three';
import { Animator, ease } from './tween.ts';

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ScreenRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const FOV = 32;
const MIN_DISTANCE = 3.2;
const MAX_DISTANCE = 80;

/**
 * Renders on demand: the board is still most of the time (players are typing), so frames
 * are only drawn while something moves. Idle phases cost nothing on a phone.
 */
export class Engine {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(FOV, 1, 0.1, 400);
  readonly animator = new Animator(() => this.invalidate());
  readonly pixelRatio: number;
  private frameQueued = false;
  private shakeUntil = 0;
  private shakeStrength = 0;
  private readonly cameraBase = new Vector3(0, 0, 20);
  private cameraMove = 0;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.renderer = new WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'default' });
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.scene.add(new AmbientLight(0xffffff, 1.6));
    const key = new DirectionalLight(0xfff0d8, 2.4);
    key.position.set(-4, 6, 10);
    this.scene.add(key);
    this.camera.position.copy(this.cameraBase);
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  get viewport(): { width: number; height: number } {
    return { width: this.canvas.clientWidth || window.innerWidth, height: this.canvas.clientHeight || window.innerHeight };
  }

  resize(): void {
    const { width, height } = this.viewport;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.invalidate();
  }

  invalidate(): void {
    if (this.frameQueued) return;
    this.frameQueued = true;
    requestAnimationFrame(this.frame);
  }

  private readonly frame = (now: number) => {
    this.frameQueued = false;
    this.animator.step(now);
    const shaking = now < this.shakeUntil;
    const amount = shaking ? this.shakeStrength * ((this.shakeUntil - now) / 320) : 0;
    this.camera.position.set(
      this.cameraBase.x + (Math.random() - 0.5) * amount,
      this.cameraBase.y + (Math.random() - 0.5) * amount,
      this.cameraBase.z,
    );
    this.renderer.render(this.scene, this.camera);
    if (this.animator.busy || shaking) this.invalidate();
  };

  /** A stamp landing: short, decaying jolt scaled to the camera distance. */
  shake(strength = 0.06): void {
    this.shakeStrength = strength * this.cameraBase.z * 0.12;
    this.shakeUntil = performance.now() + 320;
    this.invalidate();
  }

  /** Camera position that fits `box` inside the part of the screen the UI leaves free. */
  framing(box: Box, free: ScreenRect, padding = 1.18): Vector3 {
    const { width, height } = this.viewport;
    const tan = Math.tan(MathUtils.degToRad(FOV / 2));
    const freeWidth = Math.max(80, free.width);
    const freeHeight = Math.max(80, free.height);
    const byHeight = (box.h * padding * height) / (freeHeight * 2 * tan);
    const byWidth = (box.w * padding * height) / (freeWidth * 2 * tan);
    const distance = MathUtils.clamp(Math.max(byHeight, byWidth), MIN_DISTANCE, MAX_DISTANCE);
    const worldPerPixel = (2 * distance * tan) / height;
    const dx = free.x + freeWidth / 2 - width / 2;
    const dy = free.y + freeHeight / 2 - height / 2;
    return new Vector3(box.x - dx * worldPerPixel, box.y + dy * worldPerPixel, distance);
  }

  /** A new move supersedes the one in flight, so reframes never fight over the camera. */
  moveCamera(target: Vector3, duration: number): Promise<void> {
    const move = ++this.cameraMove;
    const from = this.cameraBase.clone();
    if (duration <= 0 || from.distanceTo(target) < 1e-3) {
      this.cameraBase.copy(target);
      this.invalidate();
      return Promise.resolve();
    }
    return this.animator.tween(
      duration,
      (t) => {
        if (move === this.cameraMove) this.cameraBase.lerpVectors(from, target, t);
      },
      { ease: ease.inOutCubic },
    );
  }
}
